// Helpers for linking event cards to the national crime map (/karta).
// A card should navigate to the map (zoomed to the event with the
// matching crime type pre-filtered) only when the event actually has a
// resolved location (place_gps). Events without a placed pin are
// "ospecade" and can't be shown on the map, so the card stays inert.

interface MapLinkableEvent {
  id: number;
  type: string;
  place_gps?: string | null;
  placeGps?: string | null;
  city_slug?: string | null;
  citySlug?: string | null;
  url?: string | null;
}

export function eventHasMapLocation(ev: MapLinkableEvent): boolean {
  return Boolean(ev.place_gps ?? ev.placeGps);
}

export interface EventMapLinkProps {
  to: "/karta";
  search: {
    city: string | undefined;
    typ: string;
    id: number;
    days: "1w" | "1m" | "90d";
  };
}

export function eventMapLinkProps(
  ev: MapLinkableEvent,
  opts?: { city?: string | null; days?: "1w" | "1m" | "90d" },
): EventMapLinkProps {
  const citySlug =
    opts?.city ?? ev.city_slug ?? ev.citySlug ?? undefined;
  return {
    to: "/karta",
    search: {
      city: citySlug ?? undefined,
      typ: ev.type,
      id: ev.id,
      days: opts?.days ?? "90d",
    },
  };
}

/**
 * Polisens händelse-URL i DB lagras som en relativ sökväg
 * (`/aktuellt/handelser/...`). Konvertera till absolut polisen.se-URL.
 */
export function polisenUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://polisen.se${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Bygg intern händelse-URL från lagrad polis-URL.
 * Returnerar endast vägar under /aktuellt/handelser för att undvika fel-länkning.
 */
export function eventLandingPath(url: string | null | undefined): string | null {
  if (!url) return null;

  let path = url.trim();
  if (!path) return null;

  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      if (!/polisen\.se$/i.test(u.hostname)) return null;
      path = `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return null;
    }
  }

  if (!path.startsWith("/")) path = `/${path}`;
  if (path.endsWith("/")) path = path.slice(0, -1);

  return path.startsWith("/aktuellt/handelser/") ? path : null;
}

export function eventHasPolisenUrl(ev: MapLinkableEvent): boolean {
  return Boolean(polisenUrl(ev.url ?? null));
}

export function eventHasLandingPath(ev: MapLinkableEvent): boolean {
  return Boolean(eventLandingPath(ev.url ?? null));
}

export interface EventPolisenAnchorProps {
  href: string;
  target: "_blank";
  rel: "noopener noreferrer";
}

export function eventPolisenLinkProps(
  ev: MapLinkableEvent,
): EventPolisenAnchorProps | null {
  const href = polisenUrl(ev.url ?? null);
  if (!href) return null;
  return { href, target: "_blank", rel: "noopener noreferrer" };
}

export interface EventLandingAnchorProps {
  href: string;
}

export function eventLandingLinkProps(
  ev: MapLinkableEvent,
): EventLandingAnchorProps | null {
  const href = eventLandingPath(ev.url ?? null);
  if (!href) return null;
  return { href };
}